import {useCallback} from 'react';
import {useNavigate, NavigateFunction} from 'react-router-dom';
import {useQueryClient, QueryClient} from '@tanstack/react-query';

/**
 * Типы для параметров logout action
 */
interface LogoutActionPayload {
  navigate: NavigateFunction;
  queryClient: QueryClient;
  success: (response?: any) => void;
  error: (xhr?: any) => void;
  obj: any;
}

/**
 * Тип для Redux action creator (из redux-act)
 */
type LogoutAction = (payload: LogoutActionPayload) => void;

/**
 * Тип для обработчика события (может быть вызван с event или без)
 */
type LogoutHandler = (e?: React.MouseEvent) => void;

/**
 * Хук для выполнения логаута с автоматической очисткой кеша TanStack Query
 * Инкапсулирует всю логику логаута, включая очистку кеша, чтобы не забыть её при использовании
 * 
 * @param {LogoutAction} logout - Redux action для логаута (из authActions)
 * @returns {LogoutHandler} handleLogout - функция-обработчик для вызова логаута (можно использовать как onClick)
 * 
 * @example
 * const logout = useLogout(authActions.logout);
 * <Button onClick={logout}>Выйти</Button>
 */
export const useLogout = (logout: LogoutAction): LogoutHandler => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleLogout = useCallback<LogoutHandler>((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }

    logout({
      navigate,
      queryClient,
      success: (response?: any) => {
        // success callback вызывается в саге после успешного логаута
      },
      error: (xhr?: any) => {
        // Можно добавить обработку ошибок, если нужно
        console.error('Logout error:', xhr);
      },
      obj: null
    });
  }, [logout, navigate, queryClient]);

  return handleLogout;
};

export default useLogout;
